import config from '../config';

/**
 * Utilitaires pour la conversion d'URLs dans les données de signature
 *
 * Problème: Les URLs absolues stockées dans grid_data cassent lors du changement
 * d'environnement (dev -> prod, changement de domaine)
 *
 * Solution: Stocker des chemins relatifs et reconstruire les URLs à l'affichage
 */

/**
 * Convertit les URLs absolues en chemins relatifs dans un objet grid_data
 * Utilisé avant la sauvegarde
 *
 * @param {Object} gridData - Les données de la grille avec des URLs absolues
 * @returns {Object} - Les données avec des chemins relatifs
 */
export function convertUrlsToRelative(gridData) {
    if (!gridData) return gridData;

    const backendUrl = config.backendUrl;

    // Fonction récursive pour traiter tous les objets
    const processValue = (value) => {
        if (typeof value === 'string') {
            // Convertir les URLs absolues en chemins relatifs
            if (value.startsWith(backendUrl)) {
                return value.substring(backendUrl.length);
            }
            // Gérer aussi les anciennes URLs hardcodées
            if (value.startsWith('http://localhost/signature-builder-backend')) {
                return value.substring('http://localhost/signature-builder-backend'.length);
            }
            return value;
        }

        if (Array.isArray(value)) {
            return value.map(processValue);
        }

        if (value && typeof value === 'object') {
            const processed = {};
            for (const key in value) {
                processed[key] = processValue(value[key]);
            }
            return processed;
        }

        return value;
    };

    return processValue(gridData);
}

/**
 * Convertit les chemins relatifs en URLs absolues dans un objet grid_data
 * Utilisé après le chargement
 *
 * @param {Object} gridData - Les données de la grille avec des chemins relatifs
 * @returns {Object} - Les données avec des URLs absolues
 */
export function convertUrlsToAbsolute(gridData) {
    if (!gridData) return gridData;

    const backendUrl = config.backendUrl;

    // Patterns qui indiquent un chemin relatif du backend
    const relativePatterns = [
        '/uploads/',
        '/api/uploads/',
        '/api/icons/'
    ];

    // Fonction récursive pour traiter tous les objets
    const processValue = (value) => {
        if (typeof value === 'string') {
            // Si c'est déjà une URL absolue, ne pas modifier
            if (value.startsWith('http://') || value.startsWith('https://')) {
                return value;
            }

            // Si c'est un chemin relatif du backend, construire l'URL absolue
            for (const pattern of relativePatterns) {
                if (value.startsWith(pattern)) {
                    return `${backendUrl}${value}`;
                }
            }

            return value;
        }

        if (Array.isArray(value)) {
            return value.map(processValue);
        }

        if (value && typeof value === 'object') {
            const processed = {};
            for (const key in value) {
                processed[key] = processValue(value[key]);
            }
            return processed;
        }

        return value;
    };

    return processValue(gridData);
}

/**
 * Extrait le chemin relatif d'une URL absolue
 *
 * @param {string} url - URL absolue
 * @returns {string} - Chemin relatif ou URL inchangée si externe
 */
export function extractRelativePath(url) {
    if (!url || typeof url !== 'string') return url;

    const backendUrl = config.backendUrl;

    if (url.startsWith(backendUrl)) {
        return url.substring(backendUrl.length);
    }

    // Gérer les anciennes URLs hardcodées
    if (url.startsWith('http://localhost/signature-builder-backend')) {
        return url.substring('http://localhost/signature-builder-backend'.length);
    }

    return url;
}

/**
 * Construit une URL absolue à partir d'un chemin relatif
 *
 * @param {string} path - Chemin relatif
 * @returns {string} - URL absolue
 */
export function buildAbsoluteUrl(path) {
    if (!path || typeof path !== 'string') return path;

    // Si c'est déjà une URL absolue, retourner tel quel
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }

    return `${config.backendUrl}${path}`;
}
